import { DecimalPipe } from '@angular/common';
import { Component, effect, inject, input, signal } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { toSignal } from '@angular/core/rxjs-interop';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { Review } from '../../models/review.model';
import { ReviewService } from '../../services/review.service';

@Component({
  selector: 'app-space-reviews-section',
  standalone: true,
  imports: [DecimalPipe, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './space-reviews-section.component.html',
  host: { class: 'block' },
})
export class SpaceReviewsSectionComponent {
  private reviewService = inject(ReviewService);
  private auth = inject(Auth);

  readonly spaceId = input.required<string>();
  readonly ownerId = input.required<string>();
  readonly averageRating = input<number | null>(null);
  readonly reviewCount = input(0);

  readonly reviews = signal<Review[]>([]);
  readonly loading = signal(true);
  readonly replyDrafts = signal<Record<string, string>>({});
  readonly replySavingId = signal<string | null>(null);
  readonly replyError = signal<string | null>(null);

  readonly authUser = toSignal(authState(this.auth), { initialValue: undefined });

  readonly isOwner = () => {
    const uid = this.authUser()?.uid;
    return !!uid && uid === this.ownerId();
  };

  constructor() {
    effect(() => {
      const id = this.spaceId();
      if (!id) {
        this.reviews.set([]);
        this.loading.set(false);
        return;
      }
      this.loading.set(true);
      void this.reviewService.getReviewsForSpace(id).then(rows => {
        if (this.spaceId() !== id) return;
        this.reviews.set(rows);
        this.loading.set(false);
      }).catch(() => {
        if (this.spaceId() !== id) return;
        this.reviews.set([]);
        this.loading.set(false);
      });
    });
  }

  stars(_rating: number): number[] {
    return Array.from({ length: 5 }, (_, i) => i + 1);
  }

  guestLabel(review: Review): string {
    return review.guestDisplayName?.trim() || 'Cliente';
  }

  replyDraft(reviewId: string): string {
    return this.replyDrafts()[reviewId] ?? '';
  }

  onReplyInput(reviewId: string, event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.replyDrafts.update(m => ({ ...m, [reviewId]: value }));
  }

  async submitReply(review: Review): Promise<void> {
    const id = review.id ?? review.bookingId;
    if (!id || this.replySavingId()) return;
    this.replySavingId.set(id);
    this.replyError.set(null);
    try {
      await this.reviewService.setOwnerReply(id, this.replyDraft(id), this.ownerId());
      const rows = await this.reviewService.getReviewsForSpace(this.spaceId());
      this.reviews.set(rows);
      this.replyDrafts.update(m => {
        const next = { ...m };
        delete next[id];
        return next;
      });
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? 'No se pudo guardar la respuesta.';
      this.replyError.set(msg);
    } finally {
      this.replySavingId.set(null);
    }
  }
}
